import { useEffect, useState } from 'react'
import { ConfirmationModal, Input } from '@airclou/ui'
import { AlertTriangle } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'

/**
 * "Confirm Ownership Transfer".
 *
 * Extracted from OrganizationPage for the same reason DeleteOrganizationModal
 * was: inline in the page, the only way to reach this dialog in a test is to
 * expand a collapsed card and drive a ComboBox that selects on a portal-rendered
 * `role="option"` — so the confirmation gate, which is the whole point of the
 * dialog, went untested. As its own component it renders directly.
 *
 * It owns the typed input; the caller owns the phrase to match.
 */
interface TransferOwnershipModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  /** The phrase the operator must retype. Generated per opening by the caller. */
  confirmationPhrase: string
  newOwnerName: string
  newOwnerEmail?: string
  isTransferring?: boolean
  /** Server-named failure, already translated by the page. */
  errorMessage?: string | null
}

export function TransferOwnershipModal({
  isOpen,
  onClose,
  onConfirm,
  confirmationPhrase,
  newOwnerName,
  newOwnerEmail,
  isTransferring = false,
  errorMessage = null,
}: TransferOwnershipModalProps) {
  const { t } = useTranslation('dashboard')
  const [confirmationInput, setConfirmationInput] = useState('')

  useEffect(() => {
    if (!isOpen) setConfirmationInput('')
  }, [isOpen])

  /**
   * Has the operator typed the phrase exactly?
   *
   * Case and surrounding whitespace are forgiving; the words are not. Guards
   * against an empty phrase so the button cannot be enabled in the moment
   * before one is generated.
   */
  const isConfirmationValid =
    confirmationPhrase.trim().length > 0 &&
    confirmationInput.trim().toLowerCase() === confirmationPhrase.trim().toLowerCase()

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={() => !isTransferring && onClose()}
      title={t('organization.confirmOwnershipTransfer')}
      variant="warning"
      // Wider than the default `md`. This dialog carries a four-item consequence
      // list, the new owner's name and email, and a confirmation phrase that has
      // to be read character by character — at max-w-md the phrase wrapped,
      // which makes an exact-match field needlessly hard to satisfy.
      size="xl"
      confirmText={t('organization.transferOwnership')}
      cancelText={t('common.cancel')}
      onConfirm={() => isConfirmationValid && onConfirm()}
      onCancel={onClose}
      isLoading={isTransferring}
      // Disabled until the phrase matches exactly. It used to be clickable
      // always, with the submit handler returning early on a mismatch — so a
      // wrong phrase produced a click that did nothing and said nothing, which
      // an operator cannot tell from a broken button. The delete dialog already
      // worked this way; this brings the two into line.
      confirmDisabled={!isConfirmationValid}
      showIcon={false}
    >
      <div className="space-y-4">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-800 dark:text-red-200 mb-2">
                {t('organization.confirmTransferWarning')}
              </p>
              <p className="text-red-700 dark:text-red-300">
                <Trans t={t} i18nKey="organization.transferAbout" values={{ name: newOwnerName }} />
              </p>
              <ul className="list-disc list-inside mt-2 text-red-700 dark:text-red-300 space-y-1">
                <li>{t('organization.loseBillingPermissions')}</li>
                <li>{t('organization.downgradeToAdmin')}</li>
                <li>{t('organization.newOwnerControl')}</li>
                <li>{t('organization.onlyNewOwnerTransfer')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>{t('organization.newOrgOwner')}</strong> {newOwnerName}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{newOwnerEmail}</p>
        </div>

        <div className="space-y-3">
          <div>
            <label
              htmlFor="confirmationPhrase"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('organization.typeConfirmPhrase')}
            </label>
            <div className="p-3 bg-gray-100 dark:bg-gray-800 border rounded-lg mb-3">
              <code className="text-sm font-mono text-foreground">
                {confirmationPhrase}
              </code>
            </div>
            <Input
              id="confirmationPhrase"
              value={confirmationInput}
              onChange={setConfirmationInput}
              placeholder={t('organization.confirmPhrasePlaceholder')}
              disabled={isTransferring}
              className="font-mono"
            />
          </div>
          {confirmationInput.trim().length > 0 && !isConfirmationValid && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('organization.phraseDoesntMatch')}
            </p>
          )}
        </div>
      </div>

      {errorMessage && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
      )}
    </ConfirmationModal>
  )
}
